import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { ArrowLeft, ArrowRight, MagnifyingGlassPlus, MagnifyingGlassMinus, Download, Chat, Square, Circle, ArrowUpRight, Pencil, Trash, FileText, NotePencil, CheckCircle, Lock, FilePdf } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PDFViewerSVG = ({ documentId, cycleId, onNoPdf, user, cycleData, onRefresh }) => {
  const [pageNumber, setPageNumber] = useState(1);
  const [numPages, setNumPages] = useState(1);
  const [pdfData, setPdfData] = useState(null);
  const [pdfError, setPdfError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [comments, setComments] = useState([]);
  const [markups, setMarkups] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState('');
  const [editingMarkupId, setEditingMarkupId] = useState(null);
  const [editingMarkupText, setEditingMarkupText] = useState('');
  const [scale, setScale] = useState(1.0);
  const [drawingMode, setDrawingMode] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPos, setStartPos] = useState(null);
  const [currentPath, setCurrentPath] = useState([]);
  const [selectedColor, setSelectedColor] = useState('#FF4F00');
  
  // PDF page dimensions (original, unscaled)
  const [pdfPageSize, setPdfPageSize] = useState({ width: 595, height: 842 });
  
  // Note dialog states
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [pendingMarkupData, setPendingMarkupData] = useState(null);
  const [markupNote, setMarkupNote] = useState('');
  
  // Finalize dialog states
  const [finalizeDialogOpen, setFinalizeDialogOpen] = useState(false);
  const [finalizeComments, setFinalizeComments] = useState('');
  const [isFinalized, setIsFinalized] = useState(false);
  
  // Current shape being drawn (for preview)
  const [currentShape, setCurrentShape] = useState(null);
  
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const pdfCacheRef = useRef(new Map());
  
  // Permission checks
  const isSuperAdmin = user?.is_super_admin || user?.role?.toLowerCase() === 'admin';
  const permissions = user?.permissions || {};
  const canViewComments = permissions.can_view_comments || isSuperAdmin;
  const canDownloadComments = permissions.can_download_comments || isSuperAdmin;
  const isDocumentController = user?.role?.toLowerCase() === 'document controller' || isSuperAdmin;

  // Fetch PDF
  useEffect(() => {
    if (cycleId) {
      fetchPDF();
      fetchMarkups();
      fetchComments();
      checkFinalizationStatus();
    }
  }, [cycleId]);

  const fetchPDF = async () => {
    if (!cycleId) return;
    
    try {
      setLoading(true);
      setPdfError(null);
      
      const cacheKey = cycleId;
      if (pdfCacheRef.current.has(cacheKey)) {
        setPdfData(pdfCacheRef.current.get(cacheKey));
        setLoading(false);
        return;
      }
      
      const response = await fetch(`${API}/cycles/${cycleId}/download-pdf`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          setPdfError('No PDF uploaded for this revision');
          onNoPdf?.();
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return;
      }
      
      const contentLength = response.headers.get('content-length');
      const total = parseInt(contentLength, 10) || 0;
      
      const reader = response.body.getReader();
      const chunks = [];
      let receivedLength = 0;
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        receivedLength += value.length;
        if (total > 0) {
          setLoadingProgress(Math.round((receivedLength / total) * 100));
        }
      }
      
      const blob = new Blob(chunks, { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      pdfCacheRef.current.set(cacheKey, url);
      setPdfData(url);
      
    } catch (error) {
      console.error('Failed to fetch PDF', error);
      setPdfError('Failed to load PDF');
    } finally {
      setLoading(false);
    }
  };

  const fetchMarkups = async () => {
    if (!cycleId) return;
    try {
      const response = await axios.get(`${API}/cycles/${cycleId}/markups`);
      setMarkups(response.data || []);
    } catch (error) {
      console.error('Failed to fetch markups', error);
    }
  };

  const fetchComments = async () => {
    if (!cycleId) return;
    try {
      const response = await axios.get(`${API}/cycles/${cycleId}/comments`);
      setComments(response.data || []);
    } catch (error) {
      console.error('Failed to fetch comments', error);
    }
  };

  const checkFinalizationStatus = async () => {
    if (!cycleId || !user) return;
    try {
      const response = await axios.get(`${API}/cycles/${cycleId}`);
      const cycle = response.data;
      
      // For admin users, check if the cycle is in a state that should block edits
      // Allow editing if status is not 'approved' or 'sent_to_contractor'
      if (user.role?.toLowerCase() === 'admin') {
        const blockingStatuses = ['approved', 'sent_to_contractor', 'closed'];
        setIsFinalized(blockingStatuses.includes(cycle.review_status?.toLowerCase()));
      } else if (cycle.responsible_engineer === user.id) {
        setIsFinalized(cycle.responsible_engineer_finalized);
      } else if (cycle.discipline_reviewer === user.id) {
        setIsFinalized(cycle.discipline_reviewer_finalized);
      } else {
        const userReviewer = cycle.reviewers?.find(r => r.id === user.id);
        setIsFinalized(userReviewer?.finalized || false);
      }
    } catch (error) {
      console.error('Failed to check finalization status', error);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1);
  };

  const onDocumentLoadError = (error) => {
    console.error('PDF load error:', error);
    setPdfError('Failed to load PDF document');
  };

  const onPageLoadSuccess = (page) => {
    // Get the ORIGINAL PDF page dimensions (unscaled)
    // react-pdf provides these as originalWidth/originalHeight
    // If those aren't available, use the view array from the PDF
    let width = page.originalWidth;
    let height = page.originalHeight;
    
    // Fallback: if originalWidth/Height not available, get from page view
    if (!width || !height) {
      // page.view is [x, y, width, height] in PDF points
      if (page.view && page.view.length >= 4) {
        width = page.view[2] - page.view[0];
        height = page.view[3] - page.view[1];
      } else {
        // Last resort: divide current size by scale
        width = page.width / scale;
        height = page.height / scale;
      }
    }
    
    console.log('PDF page size:', { width, height, scale });
    setPdfPageSize({ width, height });
    
    // Auto-fit large PDFs to viewport on initial load
    // Target: fit the PDF width to ~80% of the container width
    if (containerRef.current) {
      const containerWidth = containerRef.current.clientWidth - 320; // Subtract sidebar width
      const idealScale = (containerWidth * 0.9) / width;
      // Clamp scale between 0.1 and 2.0
      const newScale = Math.max(0.1, Math.min(2.0, idealScale));
      // Only adjust scale if PDF is very large (engineering drawings)
      if (width > 1200 || height > 1200) {
        setScale(Math.round(newScale * 10) / 10); // Round to 1 decimal
      }
    }
  };

  // SVG coordinate helpers - convert mouse position to PDF coordinates
  const getSVGCoordinates = (e) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    
    // Use SVG's coordinate transformation to get accurate coordinates
    // This properly handles the viewBox transformation
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    
    // Transform from screen coordinates to SVG coordinates
    const svgP = pt.matrixTransform(svg.getScreenCTM().inverse());
    
    return { x: svgP.x, y: svgP.y };
  };

  // Mouse handlers for drawing
  const handleMouseDown = (e) => {
    if (!drawingMode || isFinalized) return;
    
    const coords = getSVGCoordinates(e);
    setIsDrawing(true);
    setStartPos(coords);
    
    if (drawingMode === 'freehand') {
      setCurrentPath([coords]);
    }
  };

  const handleMouseMove = (e) => {
    if (!isDrawing || !drawingMode) return;
    
    const coords = getSVGCoordinates(e);
    
    if (drawingMode === 'freehand') {
      setCurrentPath(prev => [...prev, coords]);
    } else {
      // Preview shape
      setCurrentShape({
        type: drawingMode,
        x1: startPos.x,
        y1: startPos.y,
        x2: coords.x,
        y2: coords.y
      });
    }
  };

  const handleMouseUp = (e) => {
    if (!isDrawing || !drawingMode || !cycleId) return;
    
    const coords = getSVGCoordinates(e);
    
    // Prepare markup data using PDF coordinates directly
    // No normalization needed - we store actual PDF coordinates
    let markupData = {
      page_number: pageNumber,
      markup_type: drawingMode,
      color: selectedColor,
      stroke_width: 2,
      coordinate_version: 4  // Version 4 = direct PDF coordinates
    };

    if (drawingMode === 'freehand') {
      markupData.coordinates = { points: currentPath };
    } else {
      markupData.coordinates = {
        x1: startPos.x,
        y1: startPos.y,
        x2: coords.x,
        y2: coords.y
      };
    }

    setPendingMarkupData(markupData);
    setMarkupNote('');
    setNoteDialogOpen(true);
    
    setIsDrawing(false);
    setStartPos(null);
    setCurrentPath([]);
    setCurrentShape(null);
  };

  const handleNoteDialogConfirm = async () => {
    if (!pendingMarkupData) return;
    
    try {
      const dataToSave = {
        ...pendingMarkupData,
        text: markupNote.trim() || null
      };
      
      await axios.post(`${API}/cycles/${cycleId}/markups`, dataToSave);
      toast.success('Markup saved');
      fetchMarkups();
    } catch (error) {
      toast.error('Failed to save markup');
    } finally {
      setNoteDialogOpen(false);
      setPendingMarkupData(null);
      setMarkupNote('');
      setDrawingMode(null);
    }
  };

  const handleDeleteMarkup = async (markupId) => {
    if (isFinalized) {
      toast.error('Your review has been finalized. No changes allowed.');
      return;
    }
    if (!window.confirm('Delete this markup?')) return;
    
    try {
      await axios.delete(`${API}/cycles/markups/${markupId}`);
      toast.success('Markup deleted');
      fetchMarkups();
    } catch (error) {
      toast.error('Failed to delete markup');
    }
  };

  // Begin inline edit on a markup (author only)
  const handleStartEditMarkup = (markup) => {
    setEditingMarkupId(markup.id);
    setEditingMarkupText(markup.text || '');
  };

  const handleCancelEditMarkup = () => {
    setEditingMarkupId(null);
    setEditingMarkupText('');
  };

  const handleSaveEditMarkup = async (markupId) => {
    try {
      await axios.put(`${API}/cycles/markups/${markupId}`, {
        text: editingMarkupText
      });
      toast.success('Markup updated');
      setEditingMarkupId(null);
      setEditingMarkupText('');
      fetchMarkups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update markup');
    }
  };

  // Author-only edit on markups, blocked once PM has finalized the cycle
  const canEditMarkup = (markup) => {
    if (!user) return false;
    const isAuthor = markup.created_by === user.id;
    const cycleFinalized = !!(cycleData?.pm_review_completed);
    return isAuthor && !cycleFinalized && !isFinalized;
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    
    try {
      await axios.delete(`${API}/cycles/${cycleId}/comments/${commentId}`);
      toast.success('Comment deleted');
      fetchComments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete comment');
    }
  };

  const canDeleteComment = (comment) => {
    if (!user) return false;
    const isAuthor = comment.created_by === user.id;
    const isAdmin = user.is_super_admin || user.role?.toLowerCase() === 'admin';
    const isPM = cycleData?.project_manager === user.id;
    const isDC = user.role?.toLowerCase() === 'document controller';
    return isAuthor || isAdmin || isPM || isDC;
  };

  // Begin inline edit on a comment (author only)
  const handleStartEditComment = (comment) => {
    setEditingCommentId(comment.id);
    setEditingCommentText(comment.comment_text || comment.content || '');
  };

  const handleCancelEditComment = () => {
    setEditingCommentId(null);
    setEditingCommentText('');
  };

  const handleSaveEditComment = async (commentId) => {
    if (!editingCommentText.trim()) {
      toast.error('Comment cannot be empty');
      return;
    }
    try {
      await axios.put(`${API}/cycles/${cycleId}/comments/${commentId}`, {
        comment_text: editingCommentText
      });
      toast.success('Comment updated');
      setEditingCommentId(null);
      setEditingCommentText('');
      fetchComments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update comment');
    }
  };

  // Author-only edit, blocked once PM has finalized the cycle
  const canEditComment = (comment) => {
    if (!user) return false;
    const isAuthor = comment.created_by === user.id;
    const cycleFinalized = !!(cycleData?.pm_review_completed);
    return isAuthor && !cycleFinalized;
  };

  const handleFinalizeReview = async () => {
    try {
      await axios.post(`${API}/cycles/${cycleId}/finalize-review`, {
        review_code: '',
        comments: finalizeComments
      });
      toast.success('Review finalized');
      setFinalizeDialogOpen(false);
      setIsFinalized(true);
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to finalize review');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    try {
      await axios.post(`${API}/cycles/${cycleId}/comments`, {
        comment_text: newComment,
        page_reference: pageNumber ? String(pageNumber) : null
      });
      toast.success('Comment added');
      setNewComment('');
      fetchComments();
    } catch (error) {
      console.error('Failed to add comment', error);
      toast.error('Failed to add comment');
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/cycles/${cycleId}/download-pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `document_${cycleId}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleExportWithMarkups = async () => {
    try {
      toast.info('Generating PDF with markups...');
      const response = await axios.get(`${API}/cycles/${cycleId}/export-pdf-with-markups`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `marked_${cycleId}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF exported with markups');
    } catch (error) {
      toast.error('Failed to export PDF with markups');
    }
  };

  // Render note text box on the document at markup endpoint
  const renderNoteBox = (markup, noteX, noteY) => {
    if (!markup.text) return null;
    const boxWidth = 150;
    const boxHeight = 80;
    const pageW = pdfPageSize.width || 612;
    const pageH = pdfPageSize.height || 792;
    // Clamp so box stays within page margins
    let clampedX = noteX;
    let clampedY = noteY;
    if (clampedX + boxWidth > pageW) clampedX = pageW - boxWidth - 5;
    if (clampedX < 5) clampedX = 5;
    if (clampedY + boxHeight > pageH) clampedY = pageH - boxHeight - 5;
    if (clampedY < 5) clampedY = 5;
    return (
      <foreignObject
        x={clampedX}
        y={clampedY}
        width={boxWidth}
        height={boxHeight}
        style={{ overflow: 'visible', pointerEvents: 'none' }}
      >
        <div
          xmlns="http://www.w3.org/1999/xhtml"
          style={{
            background: '#FFFDE7',
            border: '1.5px solid ' + (markup.color || '#FF4F00'),
            borderRadius: '4px',
            padding: '4px 6px',
            fontSize: '10px',
            fontFamily: 'Arial, sans-serif',
            color: '#222',
            lineHeight: 1.35,
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            maxWidth: boxWidth + 'px',
            width: 'fit-content',
            boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
          }}
        >
          {markup.text}
        </div>
      </foreignObject>
    );
  };

  // Render SVG markup
  const renderMarkup = (markup) => {
    const coords = markup.coordinates;
    const color = markup.color || '#FF4F00';
    const strokeWidth = (markup.stroke_width || 2) / scale;
    
    switch (markup.markup_type) {
      case 'rectangle': {
        const noteX = Math.max(coords.x1, coords.x2) + 5;
        const noteY = Math.min(coords.y1, coords.y2);
        return (
          <g key={markup.id}>
            <rect
              x={Math.min(coords.x1, coords.x2)}
              y={Math.min(coords.y1, coords.y2)}
              width={Math.abs(coords.x2 - coords.x1)}
              height={Math.abs(coords.y2 - coords.y1)}
              fill="none"
              stroke={color}
              strokeWidth={strokeWidth}
            />
            {renderNoteBox(markup, noteX, noteY)}
          </g>
        );
      }
      case 'circle': {
        const radius = Math.sqrt(
          Math.pow(coords.x2 - coords.x1, 2) + Math.pow(coords.y2 - coords.y1, 2)
        );
        const noteX = coords.x1 + radius + 5;
        const noteY = coords.y1 - 10;
        return (
          <g key={markup.id}>
            <circle
              cx={coords.x1}
              cy={coords.y1}
              r={radius}
              fill="none"
              stroke={color}
              strokeWidth={strokeWidth}
            />
            {renderNoteBox(markup, noteX, noteY)}
          </g>
        );
      }
      case 'arrow': {
        const angle = Math.atan2(coords.y2 - coords.y1, coords.x2 - coords.x1);
        const arrowLen = 15 / scale;
        const arrowAngle = Math.PI / 6;
        const ax1 = coords.x2 - arrowLen * Math.cos(angle - arrowAngle);
        const ay1 = coords.y2 - arrowLen * Math.sin(angle - arrowAngle);
        const ax2 = coords.x2 - arrowLen * Math.cos(angle + arrowAngle);
        const ay2 = coords.y2 - arrowLen * Math.sin(angle + arrowAngle);
        const noteX = coords.x1 + 5;
        const noteY = coords.y1 - 5;
        return (
          <g key={markup.id}>
            <line
              x1={coords.x1}
              y1={coords.y1}
              x2={coords.x2}
              y2={coords.y2}
              stroke={color}
              strokeWidth={strokeWidth}
            />
            <line x1={coords.x2} y1={coords.y2} x2={ax1} y2={ay1} stroke={color} strokeWidth={strokeWidth} />
            <line x1={coords.x2} y1={coords.y2} x2={ax2} y2={ay2} stroke={color} strokeWidth={strokeWidth} />
            {renderNoteBox(markup, noteX, noteY)}
          </g>
        );
      }
      case 'freehand': {
        if (!coords.points || coords.points.length < 2) return null;
        const pathData = coords.points
          .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`)
          .join(' ');
        const lastPoint = coords.points[coords.points.length - 1];
        const noteX = lastPoint.x + 5;
        const noteY = lastPoint.y;
        return (
          <g key={markup.id}>
            <path
              d={pathData}
              fill="none"
              stroke={color}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {renderNoteBox(markup, noteX, noteY)}
          </g>
        );
      }
      default:
        return null;
    }
  };

  // Render current shape preview while drawing
  const renderCurrentShape = () => {
    if (!currentShape && currentPath.length === 0) return null;
    
    const color = selectedColor;
    const strokeWidth = 2 / scale;
    
    if (drawingMode === 'freehand' && currentPath.length >= 2) {
      const pathData = currentPath
        .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`)
        .join(' ');
      return (
        <path
          d={pathData}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity={0.7}
        />
      );
    }
    
    if (!currentShape) return null;
    
    const { type, x1, y1, x2, y2 } = currentShape;
    
    switch (type) {
      case 'rectangle':
        return (
          <rect
            x={Math.min(x1, x2)}
            y={Math.min(y1, y2)}
            width={Math.abs(x2 - x1)}
            height={Math.abs(y2 - y1)}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeDasharray="5,5"
            opacity={0.7}
          />
        );
      case 'circle':
        const radius = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
        return (
          <circle
            cx={x1}
            cy={y1}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeDasharray="5,5"
            opacity={0.7}
          />
        );
      case 'arrow':
        return (
          <line
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke={color}
            strokeWidth={strokeWidth}
            strokeDasharray="5,5"
            opacity={0.7}
          />
        );
      default:
        return null;
    }
  };

  // Scaled dimensions for display
  const displayWidth = pdfPageSize.width * scale;
  const displayHeight = pdfPageSize.height * scale;

  return (
    <div className="flex h-full bg-gray-100" ref={containerRef}>
      {/* Main PDF Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Toolbar */}
        <div className="bg-white border-b px-4 py-2 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            {/* Page navigation */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPageNumber(Math.max(1, pageNumber - 1))}
              disabled={pageNumber <= 1}
            >
              <ArrowLeft size={16} />
            </Button>
            <span className="text-sm">
              {pageNumber} / {numPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPageNumber(Math.min(numPages, pageNumber + 1))}
              disabled={pageNumber >= numPages}
            >
              <ArrowRight size={16} />
            </Button>
          </div>

          {/* Zoom controls */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setScale(Math.max(0.5, scale - 0.2))}
            >
              <MagnifyingGlassMinus size={16} />
            </Button>
            <span className="text-sm w-16 text-center">{Math.round(scale * 100)}%</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setScale(Math.min(3, scale + 0.2))}
            >
              <MagnifyingGlassPlus size={16} />
            </Button>
          </div>

          {/* Drawing tools */}
          {cycleId && !isFinalized && (
            <div className="flex items-center gap-1">
              <Button
                variant={drawingMode === 'rectangle' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setDrawingMode(drawingMode === 'rectangle' ? null : 'rectangle')}
                title="Rectangle"
              >
                <Square size={16} />
              </Button>
              <Button
                variant={drawingMode === 'circle' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setDrawingMode(drawingMode === 'circle' ? null : 'circle')}
                title="Circle"
              >
                <Circle size={16} />
              </Button>
              <Button
                variant={drawingMode === 'arrow' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setDrawingMode(drawingMode === 'arrow' ? null : 'arrow')}
                title="Arrow"
              >
                <ArrowUpRight size={16} />
              </Button>
              <Button
                variant={drawingMode === 'freehand' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setDrawingMode(drawingMode === 'freehand' ? null : 'freehand')}
                title="Freehand"
              >
                <Pencil size={16} />
              </Button>
              
              {/* Color picker */}
              <input
                type="color"
                value={selectedColor}
                onChange={(e) => setSelectedColor(e.target.value)}
                className="w-8 h-8 rounded cursor-pointer"
                title="Color"
              />
            </div>
          )}

          {/* Download buttons */}
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleDownloadPDF}>
              <Download size={16} className="mr-1" /> PDF
            </Button>
            {markups.length > 0 && (
              <Button variant="outline" size="sm" onClick={handleExportWithMarkups}>
                <FilePdf size={16} className="mr-1" /> Export with Markups
              </Button>
            )}
          </div>
        </div>

        {/* PDF Display Area */}
        <div className="flex-1 overflow-auto p-4 flex justify-center">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                {loadingProgress > 0 && (
                  <p className="text-sm text-muted-foreground">{loadingProgress}%</p>
                )}
              </div>
            </div>
          ) : pdfError ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <FileText size={48} className="mx-auto text-muted-foreground mb-2" />
                <p className="text-muted-foreground">{pdfError}</p>
              </div>
            </div>
          ) : pdfData ? (
            <div 
              className="relative bg-white shadow-lg" 
              style={{ width: displayWidth, height: displayHeight }}
            >
              {/* PDF Page */}
              <Document
                file={pdfData}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
              >
                <Page
                  pageNumber={pageNumber}
                  scale={scale}
                  onLoadSuccess={onPageLoadSuccess}
                  renderTextLayer={!drawingMode}
                  renderAnnotationLayer={!drawingMode}
                />
              </Document>
              
              {/* SVG Overlay for Markups */}
              <svg
                ref={svgRef}
                width={displayWidth}
                height={displayHeight}
                viewBox={`0 0 ${pdfPageSize.width} ${pdfPageSize.height}`}
                className="absolute top-0 left-0"
                style={{ 
                  pointerEvents: drawingMode ? 'auto' : 'none',
                  cursor: drawingMode ? 'crosshair' : 'default'
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={() => {
                  if (isDrawing) {
                    setIsDrawing(false);
                    setStartPos(null);
                    setCurrentPath([]);
                    setCurrentShape(null);
                  }
                }}
              >
                {/* Render saved markups for current page */}
                {markups
                  .filter(m => m.page_number === pageNumber)
                  .map(renderMarkup)}
                
                {/* Render current shape preview */}
                {renderCurrentShape()}
              </svg>
            </div>
          ) : null}
        </div>

        {/* Finalized banner */}
        {isFinalized && (
          <div className="bg-green-100 border-t border-green-200 px-4 py-2 text-center text-sm text-green-700 flex items-center justify-center gap-2">
            <Lock size={16} />
            Your review has been finalized
          </div>
        )}
      </div>

      {/* Right Sidebar - Comments & Markups */}
      <div className="w-80 bg-white border-l flex flex-col overflow-hidden">
        <div className="p-4 border-b">
          <h3 className="font-semibold flex items-center gap-2">
            <Chat size={18} />
            Comments & Markups
          </h3>
        </div>
        
        <div className="flex-1 overflow-auto p-4 space-y-4">
          {/* Markups list */}
          {markups.filter(m => m.page_number === pageNumber).length > 0 && (
            <div>
              <p className="text-xs uppercase text-muted-foreground mb-2">Markups on Page {pageNumber}</p>
              {markups.filter(m => m.page_number === pageNumber).map((markup) => {
                const markupAuthor = markup.created_by_name || 'Unknown user';
                const isEditingMk = editingMarkupId === markup.id;
                return (
                <div key={markup.id} className="p-2 bg-muted rounded-sm mb-2" data-testid={`markup-${markup.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2 flex-wrap min-w-0">
                      <div
                        className="w-4 h-4 rounded-sm shrink-0"
                        style={{ backgroundColor: markup.color }}
                      />
                      <span className="text-sm capitalize">{markup.markup_type}</span>
                      <span className="text-xs font-medium" data-testid={`markup-author-${markup.id}`}>{markupAuthor}</span>
                      {markup.is_edited && (
                        <span className="text-xs italic text-muted-foreground">(edited)</span>
                      )}
                      {markup.text && !isEditingMk && (
                        <span className="text-xs text-muted-foreground truncate max-w-32">
                          - {markup.text}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {canEditMarkup(markup) && !isEditingMk && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleStartEditMarkup(markup)}
                          className="h-6 w-6 p-0 text-blue-600 hover:text-blue-800"
                          title="Edit note"
                          data-testid={`edit-markup-${markup.id}`}
                        >
                          <Pencil size={14} />
                        </Button>
                      )}
                      {!isFinalized && !isEditingMk && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteMarkup(markup.id)}
                          className="h-6 w-6 p-0"
                          data-testid={`delete-markup-${markup.id}`}
                        >
                          <Trash size={14} />
                        </Button>
                      )}
                    </div>
                  </div>
                  {isEditingMk && (
                    <div className="mt-2 space-y-2">
                      <Textarea
                        value={editingMarkupText}
                        onChange={(e) => setEditingMarkupText(e.target.value)}
                        className="text-sm min-h-[60px]"
                        placeholder="Markup note..."
                        data-testid={`edit-markup-textarea-${markup.id}`}
                      />
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleCancelEditMarkup}
                          data-testid={`edit-markup-cancel-${markup.id}`}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleSaveEditMarkup(markup.id)}
                          data-testid={`edit-markup-save-${markup.id}`}
                        >
                          Save
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}

          {/* Comments list */}
          <div>
            <p className="text-xs uppercase text-muted-foreground mb-2">Comments</p>
            {comments.filter(c => !c.page_number || c.page_number === pageNumber).map((comment) => {
              const authorName = comment.created_by_name || comment.author_name || 'Unknown user';
              const isEditing = editingCommentId === comment.id;
              return (
              <div key={comment.id} className="p-3 bg-muted rounded-sm mb-3" data-testid={`comment-${comment.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium" data-testid={`comment-author-${comment.id}`}>{authorName}</span>
                    {(comment.discipline || comment.role) && (
                      <span className="text-xs px-2 py-0.5 bg-primary text-white rounded-sm">
                        {comment.discipline || comment.role}
                      </span>
                    )}
                    {comment.is_edited && (
                      <span className="text-xs italic text-muted-foreground">(edited)</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {canEditComment(comment) && !isEditing && (
                      <button
                        onClick={() => handleStartEditComment(comment)}
                        className="text-blue-600 hover:text-blue-800 p-1"
                        title="Edit comment"
                        data-testid={`edit-comment-${comment.id}`}
                      >
                        <Pencil size={14} />
                      </button>
                    )}
                    {canDeleteComment(comment) && !isEditing && (
                      <button
                        onClick={() => handleDeleteComment(comment.id)}
                        className="text-red-500 hover:text-red-700 p-1"
                        title="Delete comment"
                        data-testid={`delete-comment-${comment.id}`}
                      >
                        <Trash size={14} />
                      </button>
                    )}
                  </div>
                </div>
                {isEditing ? (
                  <div className="space-y-2">
                    <Textarea
                      value={editingCommentText}
                      onChange={(e) => setEditingCommentText(e.target.value)}
                      className="text-sm min-h-[70px]"
                      data-testid={`edit-comment-textarea-${comment.id}`}
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleCancelEditComment}
                        data-testid={`edit-comment-cancel-${comment.id}`}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleSaveEditComment(comment.id)}
                        data-testid={`edit-comment-save-${comment.id}`}
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm whitespace-pre-wrap">{comment.comment_text || comment.content}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  {new Date(comment.created_at ? (String(comment.created_at).endsWith('Z') || String(comment.created_at).includes('+') ? comment.created_at : comment.created_at + 'Z') : 0).toLocaleString()}
                </p>
              </div>
              );
            })}
            {comments.filter(c => !c.page_number || c.page_number === pageNumber).length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">No comments yet</p>
            )}
          </div>
        </div>

        {/* Add comment */}
        {cycleId && (
          <div className="p-4 border-t">
            <Textarea
              placeholder="Add a comment..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              className="mb-2"
              rows={3}
            />
            <div className="flex gap-2">
              <Button 
                size="sm" 
                onClick={handleAddComment}
                className="flex-1"
              >
                Add Comment
              </Button>
              {!isFinalized && (
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => setFinalizeDialogOpen(true)}
                >
                  <CheckCircle size={16} className="mr-1" />
                  Finalize
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Note Dialog */}
      <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Note to Markup</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              placeholder="Add a note (optional)..."
              value={markupNote}
              onChange={(e) => setMarkupNote(e.target.value)}
              rows={4}
            />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => {
                setNoteDialogOpen(false);
                setPendingMarkupData(null);
                setDrawingMode(null);
              }}>
                Cancel
              </Button>
              <Button onClick={handleNoteDialogConfirm}>
                Save Markup
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Finalize Dialog */}
      <Dialog open={finalizeDialogOpen} onOpenChange={setFinalizeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Finalize Review</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Once finalized, you won't be able to add or modify markups.
            </p>
            <Textarea
              placeholder="Final comments (optional)..."
              value={finalizeComments}
              onChange={(e) => setFinalizeComments(e.target.value)}
              rows={4}
            />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setFinalizeDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleFinalizeReview}>
                Finalize Review
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PDFViewerSVG;
